import { Crown, ShieldCheck } from 'lucide-react'

import { MOCK_MEMBERS } from '@/features/marketing/mockData/members'
import { cn } from '@/lib/utils'

const ROLE_ACCENT: Record<'OWNER' | 'ADMIN' | 'MEMBER', string> = {
  OWNER:  'border-warning/40 bg-warning/10 text-warning',
  ADMIN:  'border-primary/40 bg-primary/10 text-primary',
  MEMBER: 'border-border bg-bg/40 text-fg-muted',
}

/**
 * Mocked members list — visually mirrors the real `MembersTab` row.
 * Shows ~8 rows for the marketing mockup so the visual fills the
 * frame without becoming a wall of avatars.
 */
export function MembersListMockup({
  rowCount = 8,
}: {
  rowCount?: number
}) {
  const rows = MOCK_MEMBERS.slice(0, rowCount)
  return (
    <div className="divide-y divide-border/60">
      {rows.map((m) => (
        <div
          key={m.userId}
          className="flex items-center gap-3 px-4 py-3"
        >
          {/* Avatar + live dot overlay */}
          <div className="relative">
            <img
              src={m.avatarUrl}
              alt=""
              className="size-10 rounded-full border border-border object-cover"
              loading="lazy"
            />
            {m.isLive && (
              <span
                className="absolute right-0 bottom-0 inline-flex size-3 items-center justify-center rounded-full border-2 border-surface bg-success"
                aria-hidden
              />
            )}
          </div>

          {/* Name + callsign */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-display text-sm font-semibold text-fg">
                {m.displayName}
              </span>
              {m.verified && (
                <ShieldCheck className="size-3.5 shrink-0 text-primary" aria-hidden />
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="truncate text-[11px] text-fg-subtle">
                {m.callsign}
              </span>
              {m.station && (
                <span className="hidden truncate text-[11px] text-fg-subtle sm:inline">
                  · {m.station}
                </span>
              )}
            </div>
          </div>

          {/* Role pill */}
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 font-display text-[10px] font-bold tracking-[0.12em] uppercase',
              ROLE_ACCENT[m.role],
            )}
          >
            {m.role === 'OWNER' && <Crown className="size-2.5" aria-hidden />}
            {m.role.toLowerCase()}
          </span>
        </div>
      ))}
    </div>
  )
}
